// Map.ts
import pkg from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

const { Map: MapLibreMap, addProtocol, ScaleControl, GeolocateControl } = pkg

import { Protocol, PMTiles } from 'pmtiles'
import Experience from '../Experience.js'
import InfoWindow from './InfoWindow.js'
import ZoomSlider from './ZoomSlider.js'
import Title from './Title.js'
import { handleMapMove, getCenter, convertCenterToDMS } from '../Utils/MapUtils.js'

export default class Map {
    constructor() {
        this.experience = new Experience()
        this.config = this.experience.config
        this.infoWindow = new InfoWindow(this.config.infoLayers)
        this.gps = document.getElementById('gps')

        this.map = null
        this.handlers = []

        this.initMap()
    }

    debounce(func, delay) {
        let timeout
        return (...args) => {
            clearTimeout(timeout)
            timeout = setTimeout(() => func(...args), delay)
        }
    }

    updateUrl(center, zoom) {
        const url = new URL(window.location.href)

        url.searchParams.set('lat', center.lat.toFixed(5))
        url.searchParams.set('lon', center.lng.toFixed(5))
        url.searchParams.set('zoom', zoom.toFixed(2))

        window.history.replaceState({}, '', url)
    }

    async initMap() {
        try {
            const protocol = new Protocol()
            addProtocol('pmtiles', protocol.tile)

            const pmtilesUrl = this.config.style.sources.basemap.url
            const pmtiles = new PMTiles(pmtilesUrl)
            const header = await pmtiles.getHeader()

            // === URL PARAMS ===
            const params = new URLSearchParams(window.location.search)

            const lat = parseFloat(params.get('lat'))
            const lon = parseFloat(params.get('lon'))
            const zoom = parseFloat(params.get('zoom'))

            const hasValidParams =
                !isNaN(lat) && !isNaN(lon) && !isNaN(zoom)

            this.config.style.sources.basemap.url = `pmtiles://${pmtilesUrl}`

            this.map = new MapLibreMap({
                container: 'map',
                center: hasValidParams
                    ? [lon, lat]
                    : [(header.minLon + header.maxLon) / 2, (header.minLat + header.maxLat) / 2],
                zoom: hasValidParams
                    ? zoom
                    : header.minZoom + 1,
                maxZoom: header.maxZoom,
                minZoom: header.minZoom,
                style: this.config.style
            })

            this.map.on('load', () => {
                this.zoomSlider = new ZoomSlider(this.map)
                this.title = new Title(this.map)

                this.addControls()
                this.initEvents()

                // initialise URL propre si absente
                if (!hasValidParams) {
                    const center = this.map.getCenter()
                    const zoom = this.map.getZoom()
                    this.updateUrl(center, zoom)
                }
            })

        } catch (error) {
            console.error("Erreur lors du chargement des tuiles PMTiles :", error)
        }
    }

    addControls() {
        this.map.addControl(
            new ScaleControl({ maxWidth: 80, unit: 'metric' }),
            'bottom-left'
        )

        this.map.addControl(
            new GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true
                },
                trackUserLocation: true
            }),
            'top-right'
        )
    }

    initEvents() {
        this.hoveredFeatures = []
        this.infoHoveredFeatures = []

        const updateInfoHoveredFeatures = (features) => {
            this.infoHoveredFeatures = features
            this.infoWindow.updateHoveredFeatures(features)
        }

        const updateHoveredFeatures = (features) => {
            this.hoveredFeatures = features
        }

        // === URL UPDATE (debounced) ===
        const debouncedUpdateUrl = this.debounce(() => {
            const center = this.map.getCenter()
            const zoom = this.map.getZoom()
            this.updateUrl(center, zoom)
        }, 3000)

        this.map.on('moveend', debouncedUpdateUrl)
        this.map.on('zoomend', debouncedUpdateUrl)

        // hover couches info
        handleMapMove(
            this.map,
            this.config.infoLayers,
            updateInfoHoveredFeatures,
            true
        )

        // hover couches interactives
        handleMapMove(
            this.map,
            this.config.hoverLayers,
            updateHoveredFeatures,
            true
        )

        getCenter(this.map, (newCenter) => {
            this.gps.innerHTML = convertCenterToDMS(newCenter);
        });

        const updateScale = () => {
            this.title.getScaleFromZoom(this.map.getZoom())
        }

        this.map.on('zoom', updateScale)
    }
}